// ABOUTME: custom hook for fetching and managing tenant data across components
// ABOUTME: provides tenant name, slug, and loading state with automatic caching

import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { TenantInfo, useGetTenantBySlugQuery } from "../services/tenantSlice";
import { RootState } from "../store";
import { hasRole } from "../utils/roleHelpers";
import { cacheTenantBranding } from "../utils/tenantBrandingCache";

interface UseTenantReturn {
  tenantName: string | null;
  tenantData: TenantInfo | null;
  currentTenantSlug: string;
  loading: boolean;
  error: string | null;
  refreshTenantData: () => void;
}

export const useTenant = (): UseTenantReturn => {
  const { tenantSlug } = useParams();
  const user = useSelector((state: RootState) => state.auth.user);

  // get tenant slug from URL or user context
  const currentTenantSlug = tenantSlug || user?.tenantSlug || "";

  // Skip the query if no tenant slug or if user is developer (since developers can access any tenant)
  const shouldSkip = !currentTenantSlug || hasRole(user?.roles, "developer");

  const {
    data: tenantData,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useGetTenantBySlugQuery(currentTenantSlug, {
    skip: shouldSkip,
  });

  // Cache tenant branding when data is loaded
  useEffect(() => {
    if (tenantData?.branding && currentTenantSlug) {
      cacheTenantBranding(
        currentTenantSlug,
        tenantData.branding,
        tenantData.name,
      );
    }
  }, [tenantData, currentTenantSlug]);

  const refreshTenantData = () => {
    refetch();
  };

  const error = queryError
    ? "data" in queryError &&
      typeof queryError.data === "object" &&
      queryError.data &&
      "message" in queryError.data
      ? String(queryError.data.message)
      : "Failed to fetch tenant data"
    : null;

  return {
    tenantName: tenantData?.name || null,
    tenantData: tenantData || null,
    currentTenantSlug,
    loading,
    error,
    refreshTenantData,
  };
};
